import {firstValueFrom, lastValueFrom} from 'rxjs';
import {Component, ViewChild} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {Dropdown, DropdownFilterEvent} from 'primeng/dropdown';
import {PrimeNgImportsModule} from '../../../../sdk/src/prime-ng-imports';
import {CpmCCustomerUserMembershipApiModel} from '../../../../gen/cpm-c-api-ts/src/models/c-common/cpmc-customer-user-membership.api-model';
import {IBaseCRUModalComponent} from '../../../../sdk/src/components/ibase-cru-modal/ibase-cru-modal.component';
import {CpmCCustomerApiService} from '../../../../gen/cpm-c-api-ts/src/services/c-common/cpmc-customer.api-service';
import {
    CpmCGetUserRolesParams,
    CpmCUserRoleApiService
} from '../../../../gen/cpm-c-api-ts/src/services/c-common/cpmc-user-role.api-service';
import {CpmCUserApiService} from '../../../../gen/cpm-c-api-ts/src/services/c-global/cpmc-user.api-service';
import {CpmCUserApiModel} from '../../../../gen/cpm-c-api-ts/src/models/c-global/cpmc-user.api-model';
import {CpmCUserRoleApiModel} from '../../../../gen/cpm-c-api-ts/src/models/c-common/cpmc-user-role.api-model';
import {EDialogOpenMode} from '../../../../sdk/src/enums/dialog-open-mode.enum';
import {
    CpmCCustomerUserMembershipCreateApiSchema,
    CpmCCustomerUserMembershipUpdateApiSchema
} from '../../../../gen/cpm-c-api-ts/src/schemas/c-common/cpmc-customer-user-membership.api-schemas';
import {CpmCCustomerApiModel} from '../../../../gen/cpm-c-api-ts/src/models/c-common/cpmc-customer.api-model';
import {CpmCCustomerUserMembershipApiService} from '../../../../gen/cpm-c-api-ts/src/services/c-common/cpmc-customer-user-membership.api-service';
import {SDKQueryPaginatorConfig} from '../../../../sdk/src/services/query-paginator.service';
import {CpmCUserRoleApiUtils} from '../../../../gen/cpm-c-api-ts/src/utils/c-common/cpmc-user-role.api-utils';
import {ECpmCUserRoleTypeApiEnum} from '../../../../gen/cpm-c-api-ts/src/enums/cpmc-sdk.api-enums';
import {SdkImportsModule} from '../../../../sdk/src/sdk-imports';
import {DateUtils} from '../../../../sdk/src/utils/date.utils';

export class NewEditCustomerUserMembershipModalConfig {
    constructor(
        public fromGlobalView?: boolean,
        public existing?: CpmCCustomerUserMembershipApiModel
    ) {}
}

export class NewEditCustomerUserMembershipModalResult {
    constructor(public item?: CpmCCustomerUserMembershipApiModel) {}
}

@Component({
    selector: 'app-new-edit-customer-user-membership-modal',
    templateUrl: './new-edit-customer-user-membership-modal.component.html',
    imports: [ReactiveFormsModule, PrimeNgImportsModule, SdkImportsModule]
})
export class NewEditCustomerUserMembershipModalComponent extends IBaseCRUModalComponent<
    CpmCCustomerUserMembershipApiModel,
    NewEditCustomerUserMembershipModalComponent,
    NewEditCustomerUserMembershipModalConfig,
    NewEditCustomerUserMembershipModalResult
> {
    protected item!: CpmCCustomerUserMembershipApiModel;
    protected users: CpmCUserApiModel[] = [];
    protected userDropdownLoading = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    protected userDropdownRefreshTimeout: any | null = null;

    protected customers: CpmCCustomerApiModel[] = [];
    protected userRoles: CpmCUserRoleApiModel[] = [];

    @ViewChild('user_uuid', {static: true})
    protected userDropdown!: Dropdown;

    // ===============================================================
    // ===============================================================

    /**
     * Constructor
     */
    constructor() {
        super();

        this.configure('CustomerUserMembershipApiModel', (model: CpmCCustomerUserMembershipApiModel) =>
            model.user && model.customer
                ? `${model.user.email} membership of ${model.customer.name}`
                : '<Unknown customer user membership>'
        );
        this.config.width = '35rem';
        this.contentMinHeight = '20rem';
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling is done in the parent)
     */
    protected async onReady(): Promise<void> {
        this.form = new FormGroup({
            customer_uuid: new FormControl<string | null>(null, [Validators.required]),
            user_uuid: new FormControl<string | null>(null, [Validators.required]),
            user_role_uuid: new FormControl<string | null>(null, [Validators.required]),
            expiration_date: new FormControl<string | null>(null, [])
        });

        await this.parallelSequencedAsyncCritical([
            {
                m: 'Loading users...',
                f: async (): Promise<void> => {
                    if (!this.isCreationMode) return;

                    await this.refreshUserDropdownData(null);
                    this.userDropdown.onFilter.subscribe((x) => this.onUserDropdownFilter(x));
                }
            },
            {
                m: 'Loading customers...',
                f: async (): Promise<void> => {
                    if (this.config.data?.extended?.fromGlobalView === false) return;

                    this.customers = (
                        await firstValueFrom(
                            CpmCCustomerApiService.getCustomerList({}, CpmCCustomerApiService.dPaginatorConfig)
                        )
                    ).data;
                }
            },
            {
                m: 'Loading user roles...',
                f: async (): Promise<void> => {
                    if (this.config.data?.extended?.fromGlobalView === true) {
                        if (this.config.data?.extended.existing?.customer_uuid !== undefined)
                            await this.loadUserRoles(this.config.data?.extended.existing?.customer_uuid);
                        return;
                    }

                    await this.loadUserRoles();
                }
            },
            {
                m: 'Load the user customer membership...',
                f: async (): Promise<void> => {
                    if (this.dialogOpenMode === EDialogOpenMode.NEW) return;

                    this.item = await firstValueFrom(
                        CpmCCustomerUserMembershipApiService.getCustomerUserMembershipByUUID(
                            this.config.data?.extended?.existing?.uuid ?? ''
                        )
                    );
                    this.form.updateValueAndValidity();
                }
            }
        ]);

        if (this.dialogOpenMode !== EDialogOpenMode.NEW) {
            // Apply existing value if editing
            if (typeof this.item.expiration_date === 'string') {
                this.item.expiration_date = new Date(this.item.expiration_date);
            }

            if (!this.isCreationMode) {
                this.form.controls['customer_uuid']?.disable();
                this.form.controls['user_uuid'].disable();
            }
            this.users = [this.item.user!];

            this.form.patchValue(this.item);
            this.form.updateValueAndValidity();
        }

        if (this.config.data?.extended?.fromGlobalView === true) {
            // Load user groups when the customer change
            this.form.controls['customer_uuid'].valueChanges.subscribe(async () => {
                this.showLoading = true;
                this.loadingStr = 'Loading user roles...';
                await this.asyncCritical(async () => {
                    await this.loadUserRoles();
                });
                this.showLoading = false;
            });
        } else {
            this.form.removeControl('customer_uuid');
            this.form.updateValueAndValidity();
        }
    }
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onDestroy(): Promise<void> {}

    // ===============================================================
    // ===============================================================

    /**
     * On submit
     */
    protected async onSubmit() {
        if (!this.canSubmit()) {
            return;
        }

        let customerUUID: string = null!;
        if (this.config?.data?.extended?.fromGlobalView) {
            customerUUID = this.form.controls['customer_uuid']?.value;
        } else {
            const customer = this.globalsService.sessionService.getCustomer();
            if (customer) customerUUID = customer.uuid!;
        }

        const toSubmit: CpmCCustomerUserMembershipCreateApiSchema | CpmCCustomerUserMembershipUpdateApiSchema = this
            .isCreationMode
            ? {
                  customer_uuid: customerUUID,
                  user_uuid: this.form.controls['user_uuid']?.value,
                  user_role_uuid: this.form.controls['user_role_uuid']?.value,
                  expiration_date: DateUtils.toLocalISOString(this.form.controls['expiration_date']?.value)
              }
            : {
                  user_role_uuid: this.form.controls['user_role_uuid'].value,
                  expiration_date: DateUtils.toLocalISOString(this.form.controls['expiration_date']?.value)
              };

        await this.submitCU(
            null,
            CpmCCustomerUserMembershipApiService.createCustomerUserMembership,
            CpmCCustomerUserMembershipApiService.updateCustomerUserMembership,
            this.item?.uuid,
            toSubmit,
            (resultCRUD: CpmCCustomerUserMembershipApiModel) => new NewEditCustomerUserMembershipModalResult(resultCRUD)
        );
    }

    // ===============================================================
    // ===============================================================

    /**
     * Load the user roles
     */
    protected async loadUserRoles(forceCustomerUUID?: string) {
        const params: CpmCGetUserRolesParams = {
            type: ECpmCUserRoleTypeApiEnum.MULTI_SCHEMA_TENANT_ROLE
        };

        if (this.config.data?.extended?.fromGlobalView === true) {
            if (!this.form.controls['customer_uuid']?.value && !forceCustomerUUID) {
                this.userRoles = [];
                return;
            }

            params.multi_schema_tenant_uuid = forceCustomerUUID ?? this.form.controls['customer_uuid']?.value;
        }

        const userRoles = (
            await firstValueFrom(CpmCUserRoleApiService.getUserRoles(params, CpmCUserRoleApiService.dPaginatorConfig))
        ).data;
        this.userRoles = userRoles.map((x) => {
            x.name = this.formatUserRoleName(x);
            return x;
        });
    }

    // ===============================================================
    // ===============================================================

    /**
     * When the filter change in the User Dropdown
     * @param event Event data
     */
    protected onUserDropdownFilter(event: DropdownFilterEvent) {
        if (this.userDropdownRefreshTimeout) {
            clearTimeout(this.userDropdownRefreshTimeout);
        }

        this.userDropdownLoading = true;
        this.users = [];
        this.userDropdownRefreshTimeout = setTimeout(() => {
            this.refreshUserDropdownData(event.filter);
        }, 400);
    }
    /**
     * Refresh the user dropdown data
     * @param filter Search query
     */
    protected async refreshUserDropdownData(filter: string | null) {
        const paginatorConfig: SDKQueryPaginatorConfig = {
            limit: 20,
            sort: 'email',
            order: 1
        };

        if (filter) {
            paginatorConfig.filters_rows = {email: filter};
        }

        this.userDropdownLoading = true;
        const users = (await lastValueFrom(CpmCUserApiService.getUserList({}, paginatorConfig))).data;
        this.users = users;
        this.userDropdownLoading = false;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Format a UserRole name for display to add informations about the scope
     * @param userRole Input UserRole
     * @returns Formatted UserRole name
     */
    private formatUserRoleName(userRole: CpmCUserRoleApiModel) {
        const customer = this.customers?.find((x) => x.uuid == userRole.multi_schema_tenant_uuid);
        const isFromGlobalView = this.config?.data?.extended?.fromGlobalView ?? false;
        return CpmCUserRoleApiUtils.formatUserRoleName(userRole, customer, !isFromGlobalView);
    }
}
